import { useCallback, useState } from 'react'
import { AutocompleteRenderGroupParams } from '@mui/material'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { OptionGroupHeader } from '../../../../components/inputs/OptionGroupHeader'

/**
 * Autocomplete のグループ折りたたみを管理するフック
 * @param defaultCollapsedGroups 初期状態で折りたたむグループ名の配列
 * @param groupBy グループ名を返す関数
 * @param selectedValues 選択中の値（属するグループは初期展開する）
 */
export const useCollapsibleGroups = <T,>(
  defaultCollapsedGroups?: string[],
  groupBy?: (option: T) => string,
  selectedValues?: T | T[]
) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    if (!defaultCollapsedGroups) return new Set()
    const initial = new Set(defaultCollapsedGroups)
    if (groupBy && selectedValues) {
      const values = Array.isArray(selectedValues)
        ? selectedValues
        : [selectedValues]
      for (const val of values) {
        if (val) initial.delete(groupBy(val))
      }
    }
    return initial
  })

  const toggleGroup = useCallback((group: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(group)) {
        next.delete(group)
      } else {
        next.add(group)
      }
      return next
    })
  }, [])

  const renderGroup = defaultCollapsedGroups
    ? (params: AutocompleteRenderGroupParams) => {
        const isCollapsed = collapsedGroups.has(params.group)
        return (
          <li key={params.key}>
            <OptionGroupHeader
              collapsible={true}
              onClick={e => {
                e.stopPropagation()
                toggleGroup(params.group)
              }}
            >
              {params.group}
              {isCollapsed ? (
                <ExpandMore sx={{ fontSize: 16 }} />
              ) : (
                <ExpandLess sx={{ fontSize: 16 }} />
              )}
            </OptionGroupHeader>
            {!isCollapsed && params.children}
          </li>
        )
      }
    : undefined

  return { renderGroup }
}
